import { useState } from "react";
import { api, formatApiError, fmtNum, today } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, FileSpreadsheet } from "lucide-react";

const PROD_COLS = [
  ["bajo", "Bajo"], ["alto", "Alto"], ["p_diario", "P. Diario"], ["p_mensual", "P. Mensual"], ["variacion", "Variación"],
];

const cell = (v) => (v === null || v === undefined || v === "" ? "" : fmtNum(v));
const fmtFecha = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function PreciosReporte() {
  const firstOfMonth = today().slice(0, 8) + "01";
  const [fi, setFi] = useState(firstOfMonth);
  const [ff, setFf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const consultar = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await api.get(`/precios/reporte?fecha_inicial=${fi}&fecha_final=${ff}`);
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const exportar = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await api.get(`/precios/reporte/export?fecha_inicial=${fi}&fecha_final=${ff}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `precios_${fi}_a_${ff}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Reporte exportado a Excel");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo exportar");
    } finally {
      setExporting(false);
    }
  };

  const input = "rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs text-slate-500 block">Fecha Inicial</label>
          <input data-testid="reporte-fecha-inicial" type="date" className={input} value={fi} onChange={(e) => setFi(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500 block">Fecha Final</label>
          <input data-testid="reporte-fecha-final" type="date" className={input} value={ff} onChange={(e) => setFf(e.target.value)} />
        </div>
        <button data-testid="reporte-consultar" onClick={consultar} disabled={loading}
          className="bg-slate-900 text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-slate-800 flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Consultar
        </button>
        {data && (
          <button data-testid="reporte-export" onClick={exportar} disabled={exporting}
            className="ml-auto rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-600" />} Exportar Excel
          </button>
        )}
      </div>

      {loading && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-16 text-center" data-testid="reporte-loading">
          <Loader2 className="h-8 w-8 text-slate-400 animate-spin mx-auto" />
          <p className="mt-3 text-slate-600 font-medium">Generando reporte...</p>
        </div>
      )}

      {!loading && data && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="reporte-table">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th rowSpan={2} className="px-3 py-2 text-left border border-slate-700 align-middle">FECHA</th>
                  <th colSpan={5} className="px-3 py-2 text-center border border-slate-700">PROPANO</th>
                  <th colSpan={5} className="px-3 py-2 text-center border border-slate-700">BUTANO</th>
                  <th rowSpan={2} className="px-3 py-2 text-center border border-slate-700 align-middle">DOF</th>
                </tr>
                <tr className="bg-slate-800 text-white">
                  {PROD_COLS.map(([k, l]) => <th key={`p-${k}`} className="px-3 py-1.5 text-right border border-slate-700 font-medium">{l}</th>)}
                  {PROD_COLS.map(([k, l]) => <th key={`b-${k}`} className="px-3 py-1.5 text-right border border-slate-700 font-medium">{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => {
                  const vacio = !row.propano.bajo && !row.butano.bajo && row.dof == null;
                  return (
                    <tr key={row.fecha} className={vacio ? "bg-slate-50" : "hover:bg-slate-50/50"}>
                      <td className="px-3 py-1.5 border border-slate-200 font-medium text-slate-900 whitespace-nowrap">{fmtFecha(row.fecha)}</td>
                      {PROD_COLS.map(([k]) => <td key={`p-${k}`} className="px-3 py-1.5 border border-slate-200 text-right tabular-nums">{cell(row.propano[k])}</td>)}
                      {PROD_COLS.map(([k]) => <td key={`b-${k}`} className="px-3 py-1.5 border border-slate-200 text-right tabular-nums">{cell(row.butano[k])}</td>)}
                      <td className="px-3 py-1.5 border border-slate-200 text-right tabular-nums font-medium">{row.dof != null ? fmtNum(row.dof) : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-semibold">
                  <td colSpan={11} className="px-3 py-2 border border-slate-200 text-right text-slate-600">Promedio DOF</td>
                  <td className="px-3 py-2 border border-slate-200 text-right tabular-nums" data-testid="reporte-promedio">{data.promedio_dof != null ? fmtNum(data.promedio_dof) : "—"}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {!loading && !data && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-16 text-center text-slate-500" data-testid="reporte-initial">
          Selecciona un rango de fechas y presiona <span className="font-medium text-slate-900">Consultar</span> para generar el reporte.
        </div>
      )}
    </div>
  );
}
